/**
 * Menu state machine + config draft + Scenario emission (v0M3.7).
 *
 * The start-menu system is a small state machine over 4 screens:
 *   'main' → New Game → 'mode' → Standard → 'standard-config' → Start → 'in-game'
 *
 * Each screen renders a DOM overlay; transitions swap the overlay and
 * invoke re-render. The state machine is UI-framework agnostic (no React
 * / Vue / etc) — pure data + callbacks. Screen renderers subscribe via
 * `onStateChange` to redraw when transitions fire.
 *
 * Config draft — the Standard Config screen mutates a `StandardConfigDraft`
 * as the user picks slots / difficulty / etc. On "Start", `emitScenario`
 * validates + returns a fully-typed `Scenario` ready for
 * `bootstrapScenario`. Validation errors show inline on the config screen
 * (exactly-one-Player rule is the main constraint).
 *
 * Design locks embedded here (Michael, 2026-07-12):
 *  - 6 fixed slots, one per v1 faction in canonical order.
 *  - Each slot's factionName is fixed (defined by the slot index → faction
 *    map). Only `role` is user-configurable.
 *  - Player color is NOT configurable — canonical per faction.
 *  - Difficulty is captured but unconsumed at v0M3.7 (v0M3.8 AI reads it).
 *  - Map is a dropdown; Karth only at v0M3.7 (more maps land later).
 */

import { listFactions } from '../../data/factions';
import type {
  Difficulty,
  Scenario,
  SlotConfig,
  SlotRole,
  StartingMode,
  VictoryCondition,
} from '../../sim/scenario';

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------

export type MenuScreen = 'main' | 'mode' | 'standard-config' | 'in-game';

// ---------------------------------------------------------------------------
// Draft — mutable working copy of the Standard Config form
// ---------------------------------------------------------------------------

/**
 * One slot's user-controlled fields. `factionName` is baked in per slot
 * index (see `initialDraft`) and NOT user-editable — v0M3.7 renders it
 * as a fixed label; only `role` toggles.
 */
export interface SlotDraft {
  readonly factionName: string;
  role: SlotRole;
}

export interface StandardConfigDraft {
  slots: [SlotDraft, SlotDraft, SlotDraft, SlotDraft, SlotDraft, SlotDraft];
  difficulty: Difficulty;
  victoryCondition: VictoryCondition;
  startingMode: StartingMode;
  mapName: string;
  seed: number;
  /** v0M5.2 (2026-07-25) — Map exploration at scenario start. false =
   *  Map Unexplored (default, everything black); true = Map Explored
   *  (terrain visible from turn 1, live enemies still gated by vision). */
  startExplored: boolean;
}

/**
 * Fresh draft with sensible defaults: 6 slots (Rome, Macedon, Persia,
 * Carthage, Egypt, Gauls); Rome=Player, rest=AI; Normal difficulty;
 * destroy-capitals; Single Province; Karth; random seed.
 */
export function initialDraft(): StandardConfigDraft {
  const factions = listFactions();
  if (factions.length < 6) {
    throw new Error('Cannot build draft: faction registry has < 6 factions.');
  }
  const slots = factions.slice(0, 6).map<SlotDraft>((f, i) => ({
    factionName: f.name,
    role: i === 0 ? 'player' : 'ai',
  })) as StandardConfigDraft['slots'];
  return {
    slots,
    difficulty: 'normal',
    victoryCondition: 'destroy-capitals',
    startingMode: 'single-province',
    mapName: 'karth',
    // 32-bit random seed. Deterministic replay = paste this into the seed
    // field on a subsequent run.
    seed: Math.floor(Math.random() * 0x100000000) >>> 0,
    startExplored: false,
  };
}

// ---------------------------------------------------------------------------
// Slot role editing with exactly-one-Player rule
// ---------------------------------------------------------------------------

/**
 * Change a slot's role. If the new role is 'player', any OTHER slot that
 * was 'player' is demoted to 'ai' — enforces the "exactly one Player"
 * invariant client-side so the user can't create an invalid config.
 */
export function setSlotRole(draft: StandardConfigDraft, slotIndex: number, role: SlotRole): void {
  const slot = draft.slots[slotIndex];
  if (!slot) throw new Error(`setSlotRole: slot index ${slotIndex} out of range (0-5).`);
  if (role === 'player') {
    // Demote any existing player.
    for (let i = 0; i < draft.slots.length; i++) {
      if (i !== slotIndex && draft.slots[i]!.role === 'player') {
        draft.slots[i]!.role = 'ai';
      }
    }
  }
  slot.role = role;
  // If we just demoted the only Player and left none, promote back to Player.
  const hasPlayer = draft.slots.some((s) => s.role === 'player');
  if (!hasPlayer) {
    slot.role = 'player';
  }
}

// ---------------------------------------------------------------------------
// Scenario emission
// ---------------------------------------------------------------------------

export type EmissionResult =
  | { readonly ok: true; readonly scenario: Scenario }
  | { readonly ok: false; readonly error: string };

/**
 * Validate the draft and return an emission result. On success, callers
 * hand the Scenario to `bootstrapScenario`. On failure, callers display
 * `error` inline on the config screen. Validation mirrors
 * `validateScenario` in scenario.ts but runs earlier (before bootstrap
 * throws).
 */
export function emitScenario(draft: StandardConfigDraft): EmissionResult {
  const players = draft.slots.filter((s) => s.role === 'player');
  if (players.length === 0) {
    return { ok: false, error: 'You must have exactly one Player slot.' };
  }
  if (players.length > 1) {
    return { ok: false, error: `You have ${players.length} Player slots — pick exactly one.` };
  }
  const activeCount = draft.slots.filter((s) => s.role !== 'neutral').length;
  if (activeCount < 2) {
    return {
      ok: false,
      error: 'You need at least 2 active slots (Player + AI) to play a battle.',
    };
  }

  const scenario: Scenario = {
    mapName: draft.mapName,
    seed: draft.seed,
    slots: draft.slots.map<SlotConfig>((s) => ({
      factionName: s.factionName,
      role: s.role,
    })),
    difficulty: draft.difficulty,
    victoryCondition: draft.victoryCondition,
    startingMode: draft.startingMode,
    startExplored: draft.startExplored,
  };
  return { ok: true, scenario };
}

// ---------------------------------------------------------------------------
// State machine
// ---------------------------------------------------------------------------

export interface MenuState {
  screen: MenuScreen;
  draft: StandardConfigDraft;
  /** Transient toast/banner text for "coming soon" clicks + errors. Cleared on next transition. */
  banner: string | null;
}

export function createMenuState(): MenuState {
  return { screen: 'main', draft: initialDraft(), banner: null };
}

/** Set a banner message ('coming soon', validation error, etc). */
export function setBanner(state: MenuState, text: string | null): void {
  state.banner = text;
}

/**
 * Screen transitions. Every transition clears the banner (except
 * `setBanner` which sets it). Callers dispatch through these helpers
 * rather than mutating `screen` directly.
 */
export function goToMain(state: MenuState): void {
  state.screen = 'main';
  state.banner = null;
}
export function goToMode(state: MenuState): void {
  state.screen = 'mode';
  state.banner = null;
}
export function goToStandardConfig(state: MenuState): void {
  state.screen = 'standard-config';
  state.banner = null;
}
export function goInGame(state: MenuState): void {
  state.screen = 'in-game';
  state.banner = null;
}
